import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type ActivityRecord,
  type CostAccountRow,
  type CostServiceRow,
  type CostBandStatus,
} from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { getActionMeta } from '../lib/activityLabels';
import { Waypoints, Search, ChevronDown, ChevronRight, Link2, RefreshCw } from 'lucide-react';

// ── Founder Trace View (ob-A5, audit-control-observatory) ───────────────────
//
// Built INTO Lisa's founder audit dashboard structure (Observability.tsx /
// ActivityLog.tsx) — same Card/SETTLE/MORPH/cn idiom, same founder-only
// gating, its own page + sidebar entry (matching how Activity Log and
// Observability already sit as siblings, not tabs inside one page). Two
// in-page tabs of its own: Trace (actor timeline / correlation chain) and
// Costs (estate cost tiles) — see the Sidebar.tsx wiring for the entry point.
//
// correlationId is read from EITHER the top-level attribute (once the
// parallel correlation-id-spine PR lands) OR the older nested
// details.correlationId shape (Discord-command audit rows today) — see
// recordCorrelationId() below.

function recordCorrelationId(record: ActivityRecord): string | undefined {
  if (record.correlationId) return record.correlationId;
  const nested = record.details?.correlationId;
  return typeof nested === 'string' ? nested : undefined;
}

// ── Trace tab ─────────────────────────────────────────────────────────────

type SearchMode = 'actor' | 'correlation';

function TraceRow({ record, onViewChain }: { record: ActivityRecord; onViewChain: (correlationId: string) => void }) {
  const [open, setOpen] = useState(false);
  const meta = getActionMeta(record.action);
  const [colorText, colorBg] = meta.color.split(' ');
  const correlationId = recordCorrelationId(record);
  const detailEntries = Object.entries(record.details ?? {}).filter(([, v]) => v !== undefined && v !== null);

  return (
    <div className="rounded-lg bg-white/[0.03]">
      <button
        onClick={() => setOpen(v => !v)}
        className={cn('flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/[0.02]', MORPH)}
      >
        {open ? (
          <ChevronDown className="mt-1 h-3 w-3 shrink-0 text-gray-500" />
        ) : (
          <ChevronRight className="mt-1 h-3 w-3 shrink-0 text-gray-500" />
        )}
        <span className={cn('shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase', colorText, colorBg)}>
          {meta.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-mono text-gray-400">{record.actorId ?? 'system'}</span>
            {record.target && (
              <>
                <span className="text-gray-600">→</span>
                <span className="truncate font-mono text-gray-400">{record.target}</span>
              </>
            )}
            <span className="rounded bg-white/[0.04] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">
              {record.source}
            </span>
          </div>
        </div>
        <span className="shrink-0 text-[10px] text-gray-600">
          {new Date(record.createdAt).toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
          })}
        </span>
      </button>

      {open && (
        <div className="border-t border-white/[0.04] px-3 py-2.5 pl-9">
          {detailEntries.length > 0 ? (
            <dl className="space-y-1">
              {detailEntries.map(([k, v]) => (
                <div key={k} className="flex gap-2 text-[11px]">
                  <dt className="shrink-0 font-mono text-gray-600">{k}:</dt>
                  <dd className="break-all font-mono text-gray-400">
                    {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-[11px] text-gray-600">No details recorded.</p>
          )}
          {correlationId && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewChain(correlationId); }}
              className={cn('mt-2 flex items-center gap-1 text-[11px] font-semibold text-accent-500 hover:text-accent-400', MORPH)}
            >
              <Link2 className="h-3 w-3" /> View correlation chain
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TraceTab({ pendingCorrelationId, onConsumePending }: { pendingCorrelationId?: string; onConsumePending: () => void }) {
  const [mode, setMode] = useState<SearchMode>('actor');
  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [activeMode, setActiveMode] = useState<SearchMode>('actor');
  // Actor-mode pagination cursor — "Load older" re-runs the query with the
  // previous page's nextToken, replacing the visible page (not an
  // accumulating infinite-scroll list) to keep the state simple.
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);

  // A "View correlation chain" click from the Live firehose tab hands a
  // correlationId here (via the parent) — pick it up and run it once.
  useEffect(() => {
    if (!pendingCorrelationId) return;
    setQuery(pendingCorrelationId);
    setMode('correlation');
    setActiveQuery(pendingCorrelationId);
    setActiveMode('correlation');
    setNextToken(undefined);
    onConsumePending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCorrelationId]);

  const actorQuery = useQuery({
    queryKey: ['admin', 'observatory', 'actor', activeQuery, nextToken],
    queryFn: () => api.admin.observatoryActor(activeQuery, { limit: 50, nextToken }),
    enabled: activeMode === 'actor' && !!activeQuery,
  });

  const traceQuery = useQuery({
    queryKey: ['admin', 'observatory', 'trace', activeQuery],
    queryFn: () => api.admin.observatoryTrace(activeQuery),
    enabled: activeMode === 'correlation' && !!activeQuery,
  });

  const runSearch = (q: string, m: SearchMode) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setActiveQuery(trimmed);
    setActiveMode(m);
    setNextToken(undefined);
  };

  const viewChain = (correlationId: string) => {
    setQuery(correlationId);
    setMode('correlation');
    runSearch(correlationId, 'correlation');
  };

  const isLoading = activeMode === 'actor' ? actorQuery.isLoading : traceQuery.isLoading;
  const items: ActivityRecord[] = activeMode === 'actor' ? (actorQuery.data?.items ?? []) : (traceQuery.data?.items ?? []);
  const note = activeMode === 'correlation' ? traceQuery.data?.note : undefined;
  const pageNextToken = activeMode === 'actor' ? actorQuery.data?.nextToken : undefined;

  return (
    <div>
      <Card className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            {(['actor', 'correlation'] as const).map(m => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={cn(
                  'rounded-md px-2 py-1 text-xs transition-colors',
                  mode === m ? 'bg-white/[0.07] text-accent-500' : 'text-gray-500 hover:text-gray-300',
                )}
              >
                {m === 'actor' ? 'Actor' : 'Correlation'}
              </button>
            ))}
          </div>
          <div className="flex min-w-[220px] flex-1 gap-2">
            <input
              type="text"
              placeholder={mode === 'actor' ? 'Discord ID…' : 'Correlation ID…'}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runSearch(query, mode)}
              className="input flex-1 font-mono"
            />
            <button onClick={() => runSearch(query, mode)} className={cn('btn-primary px-3', MORPH)}>
              <Search className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {activeQuery && (
          <p className="mt-2 text-xs text-ink-secondary">
            Showing {activeMode === 'actor' ? 'actor timeline' : 'correlation chain'} for:{' '}
            <span className="font-mono font-semibold text-ink-primary">{activeQuery}</span>
          </p>
        )}
      </Card>

      {!activeQuery ? (
        <Card>
          <p className="text-sm text-gray-500">
            Search by Discord actor ID, or switch to Correlation and paste a correlation id (or click
            "View correlation chain" on any row below once you have a timeline open).
          </p>
        </Card>
      ) : isLoading ? (
        <PageSpinner />
      ) : items.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">{note ?? 'No activity found for this query.'}</p>
        </Card>
      ) : (
        <div className={cn('space-y-1.5', SETTLE)} style={settleDelay(2)}>
          {items.map((r, i) => (
            <TraceRow key={`${r.sk}-${i}`} record={r} onViewChain={viewChain} />
          ))}
          {activeMode === 'actor' && pageNextToken && (
            <button
              onClick={() => setNextToken(pageNextToken)}
              className={cn('w-full rounded-lg bg-white/[0.03] py-2 text-center text-xs text-gray-500 transition-colors hover:text-gray-300', MORPH)}
            >
              Load older →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Live tab ──────────────────────────────────────────────────────────────
// Global TIMELINE firehose (GET /admin/observatory/timeline) — day-bounded,
// auto-refreshing like Observability.tsx's live tail. "View correlation
// chain" on a row hands off to the Trace tab via onViewChain.

function FirehoseTab({ onViewChain }: { onViewChain: (correlationId: string) => void }) {
  const [day, setDay] = useState('');
  const [live, setLive] = useState(true);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admin', 'observatory', 'timeline', day],
    queryFn: () => api.admin.observatoryTimeline({ day: day || undefined, limit: 100 }),
    refetchInterval: live ? 8000 : false,
  });

  const items = data?.items ?? [];

  return (
    <div>
      <Card className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={day}
            onChange={e => setDay(e.target.value)}
            className="input font-mono text-xs"
          />
          {day && (
            <button onClick={() => setDay('')} className="btn-ghost text-xs">Clear</button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setLive(v => !v)}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors',
                live ? 'bg-green-500/10 text-green-400' : 'text-gray-500 hover:text-gray-300',
              )}
              title={live ? 'Live tail on (refreshing every 8s)' : 'Live tail off'}
            >
              <span className={cn('h-2 w-2 rounded-full', live ? 'animate-pulse bg-green-500' : 'bg-gray-600')} />
              {live ? 'Live' : 'Paused'}
            </button>
            <button onClick={() => refetch()} className={cn('btn-ghost px-2', MORPH)} title="Refresh now">
              <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            </button>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <PageSpinner />
      ) : items.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">No global activity {day ? `for ${day}` : 'yet'}.</p>
        </Card>
      ) : (
        <div className={cn('space-y-1.5', SETTLE)} style={settleDelay(2)}>
          {items.map((r, i) => (
            <TraceRow key={`${r.sk}-${i}`} record={r} onViewChain={onViewChain} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Costs tab ─────────────────────────────────────────────────────────────

const BAND_META: Record<CostBandStatus, { label: string; dot: string; text: string }> = {
  ok: { label: 'OK', dot: 'bg-green-500', text: 'text-green-400' },
  warn: { label: 'Warn', dot: 'bg-amber-500', text: 'text-amber-400' },
  alert: { label: 'Alert', dot: 'bg-red-500', text: 'text-red-400' },
};

function fmtUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

// Hand-rolled sparkline (no chart library) — de-emphasis gray line, current
// point picked out in accent. Only ever renders the estate-MTD trend across
// the fetched snapshots, oldest → newest.
function MtdSparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const w = 100;
  const h = 24;
  const pad = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return [x, y] as const;
  });
  const path = points.map(([x, y]) => `${x},${y}`).join(' ');
  const [lastX, lastY] = points[points.length - 1];
  return (
    <svg width={w} height={h} className="mt-1.5 overflow-visible" aria-hidden="true">
      <polyline points={path} fill="none" stroke="currentColor" strokeWidth={1.5} className="text-gray-600" />
      <circle cx={lastX} cy={lastY} r={2.5} className="fill-accent-500" />
    </svg>
  );
}

// Simple horizontal bar row — sequential single hue, magnitude comparison
// (no chart library). ≥4px min width so a near-zero value still reads as a mark.
function MagnitudeBar({ label, value, max, barClassName }: { label: string; value: number; max: number; barClassName: string }) {
  const pct = max > 0 ? Math.max(4, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="w-28 shrink-0 truncate text-[11px] text-gray-400">{label}</span>
      <div className="h-2 flex-1 rounded-full bg-white/[0.04]">
        <div className={cn('h-2 rounded-full', barClassName)} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-16 shrink-0 text-right font-mono text-[11px] text-gray-400">{fmtUsd(value)}</span>
    </div>
  );
}

function CostsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'observatory', 'costs'],
    queryFn: () => api.admin.observatoryCosts(30),
  });

  const snapshots = data?.items ?? []; // newest-first
  const latest = snapshots[0];
  const mtdTrend = [...snapshots].reverse().map(s => s.estateMtdUsd); // oldest -> newest

  if (isLoading) return <PageSpinner />;

  if (!latest) {
    return (
      <Card>
        <p className="text-sm text-gray-500">
          No cost snapshots yet — the daily CostMonitor digest hasn't written a COST_SNAPSHOT row for
          this table. Tiles populate automatically once it runs.
        </p>
      </Card>
    );
  }

  const band = BAND_META[latest.bandStatus] ?? BAND_META.ok;
  const maxAccount = Math.max(1, ...latest.byAccount.map((a: CostAccountRow) => a.mtdUsd));
  const maxService = Math.max(1, ...latest.topServices.map((s: CostServiceRow) => s.usd));

  return (
    <div className="space-y-4">
      <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-3', SETTLE)} style={settleDelay(0)}>
        <Card>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Estate MTD</p>
          <p className="mt-1 text-xl font-bold text-white">{fmtUsd(latest.estateMtdUsd)}</p>
          <MtdSparkline values={mtdTrend} />
        </Card>
        <Card>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Forecast (month-end)</p>
          <p className="mt-1 text-xl font-bold text-white">{fmtUsd(latest.forecastUsd)}</p>
        </Card>
        <Card>
          <div className="flex items-center gap-2">
            <span className={cn('h-2.5 w-2.5 rounded-full', band.dot)} />
            <p className={cn('text-[10px] font-bold uppercase tracking-wider', band.text)}>{band.label}</p>
          </div>
          <p className="mt-1 text-xl font-bold text-white">Band status</p>
          {latest.reasons.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {latest.reasons.map((r, i) => (
                <li key={i} className="text-[10px] text-gray-600">{r}</li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className={cn('grid grid-cols-1 gap-4 md:grid-cols-2', SETTLE)} style={settleDelay(1)}>
        <Card>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">By account (MTD)</p>
          {latest.byAccount.length === 0 ? (
            <p className="text-xs text-gray-600">(no spend)</p>
          ) : (
            <div className="space-y-2">
              {latest.byAccount.map((a: CostAccountRow) => (
                <MagnitudeBar key={a.id} label={a.name} value={a.mtdUsd} max={maxAccount} barClassName="bg-accent-500" />
              ))}
            </div>
          )}
        </Card>
        <Card>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Top services (MTD)</p>
          {latest.topServices.length === 0 ? (
            <p className="text-xs text-gray-600">(no spend)</p>
          ) : (
            <div className="space-y-2">
              {latest.topServices.map((s: CostServiceRow) => (
                <MagnitudeBar key={s.service} label={s.service} value={s.usd} max={maxService} barClassName="bg-sky-500" />
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function TraceView() {
  const [tab, setTab] = useState<'trace' | 'live' | 'costs'>('trace');
  // Cross-tab handoff: a "View correlation chain" click on the Live tab jumps
  // to Trace + Correlation mode with this id pre-filled (see TraceTab's
  // pendingCorrelationId effect above).
  const [pendingCorrelationId, setPendingCorrelationId] = useState<string | undefined>(undefined);

  const jumpToChain = (correlationId: string) => {
    setPendingCorrelationId(correlationId);
    setTab('trace');
  };

  return (
    <div>
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <Waypoints className="h-7 w-7 text-accent-500" />
        <div>
          <h1 className="text-2xl font-bold text-white">Trace View</h1>
          <p className="text-xs text-gray-500">
            Actor timelines, correlation chains, a live firehose, and estate cost tiles — the
            founder-only debugging surface for the audit spine.
          </p>
        </div>
      </div>

      <div className={cn('mb-6 flex items-center gap-1', SETTLE)} style={settleDelay(1)}>
        {(['trace', 'live', 'costs'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors',
              tab === t ? 'bg-white/[0.07] text-accent-500' : 'text-gray-500 hover:text-gray-300',
            )}
          >
            {t === 'trace' ? 'Trace' : t === 'live' ? 'Live' : 'Costs'}
          </button>
        ))}
      </div>

      {tab === 'trace' && (
        <TraceTab pendingCorrelationId={pendingCorrelationId} onConsumePending={() => setPendingCorrelationId(undefined)} />
      )}
      {tab === 'live' && <FirehoseTab onViewChain={jumpToChain} />}
      {tab === 'costs' && <CostsTab />}
    </div>
  );
}
